import type { CmpConfig } from "../core/config";
import { resolveCategories } from "../core/categories";
import { resolveBannerTranslation } from "../core/translation-resolve";
import type { ConsentState } from "../core/consent";
import { CMP_PREFERENCE_INTRO_TEXTS } from "./compliance-texts";
import { removeConsentWidget, syncConsentWidget } from "./consent-widget";
import { acquirePageLock, isStrictPageBlock, releasePageLockIfIdle } from "./page-lock";
import { getStyles } from "./styles";

export function renderPreferences(
  config: CmpConfig,
  currentConsent: ConsentState,
  onSave: (consent: ConsentState) => void,
  onClose: () => void,
  options?: { lockPage?: boolean }
): HTMLElement {
  // Ensure styles are injected (banner may not have been shown when opening via CMP.showPreferences())
  if (!document.getElementById("cmp-styles")) {
    const styleEl = document.createElement("style");
    styleEl.id = "cmp-styles";
    styleEl.textContent = getStyles(config.themeJson);
    document.head.appendChild(styleEl);
  }

  const lang = config.defaultLanguage || "tr";
  const translation = resolveBannerTranslation(config, lang);
  const buttons = translation.buttonsJson || {};

  const isTr = lang === "tr";
  const complianceMode = config.complianceMode || "Global";
  const introTexts = CMP_PREFERENCE_INTRO_TEXTS[complianceMode] || CMP_PREFERENCE_INTRO_TEXTS.Global;
  const introBody = isTr ? introTexts.tr : introTexts.en;
  const fallback = {
    managePreferences: isTr ? "Tercihleri Yönet"  : "Manage Preferences",
    save:              isTr ? "Kaydet"             : "Save",
    close:             isTr ? "Kapat"              : "Close",
  };
  const state: ConsentState = {
    categories: { ...currentConsent.categories },
    cookies: { ...currentConsent.cookies },
  };

  removeConsentWidget();
  acquirePageLock(options?.lockPage !== false);

  const overlay = document.createElement("div");
  overlay.id = "cmp-overlay";
  overlay.className = "cmp-overlay";
  document.body.appendChild(overlay);

  // Modal
  const modal = document.createElement("div");
  modal.id = "cmp-prefs";
  modal.className = "cmp-prefs";
  modal.setAttribute("role", "dialog");
  modal.setAttribute("aria-label", "Cookie Preferences");

  const categories = resolveCategories(config, lang);
  let categoriesHtml = "";
  const sorted = [...categories].sort((a, b) => a.sortOrder - b.sortOrder);

  for (const cat of sorted) {
    const name = cat.nameJson?.[lang] || cat.nameJson?.["en"] || cat.key;
    const desc = cat.descJson?.[lang] || cat.descJson?.["en"] || "";
    const checked = state.categories[cat.key] ? "checked" : "";
    const disabled = cat.required ? "disabled" : "";
    const cookieRows = (config.cookies || [])
      .filter((cookie) => cookie.category === cat.key)
      .map((cookie) => {
        const allowed = state.cookies[cookie.key] ?? state.categories[cat.key];
        const description = cookie.purposeJson?.[lang] || cookie.purposeJson?.en || "";
        return `
          <div class="cmp-prefs__cookie">
            <div class="cmp-prefs__cookie-info">
              <div class="cmp-prefs__cookie-name">${escapeHtml(cookie.cookieName)}</div>
              <div class="cmp-prefs__cookie-meta">${escapeHtml(cookie.cookieDomain)}${cookie.vendorName ? ` · ${escapeHtml(cookie.vendorName)}` : ""}</div>
              ${description ? `<div class="cmp-prefs__cookie-desc">${escapeHtml(description)}</div>` : ""}
            </div>
            <label class="cmp-toggle">
              <input type="checkbox" data-cmp-cookie="${cookie.key}" ${allowed ? "checked" : ""} ${disabled}>
              <span class="cmp-toggle__slider"></span>
            </label>
          </div>
        `;
      })
      .join("");

    categoriesHtml += `
      <div class="cmp-prefs__category">
        <div class="cmp-prefs__cat-info">
          <div class="cmp-prefs__cat-name">${escapeHtml(name)}</div>
          <div class="cmp-prefs__cat-desc">${escapeHtml(desc)}</div>
        </div>
        <label class="cmp-toggle">
          <input type="checkbox" data-cmp-cat="${cat.key}" ${checked} ${disabled}>
          <span class="cmp-toggle__slider"></span>
        </label>
      </div>
      ${config.granularCookieMode && cookieRows ? `<div class="cmp-prefs__cookies">${cookieRows}</div>` : ""}
    `;
  }

  modal.innerHTML = `
    <h2 class="cmp-prefs__title">${escapeHtml(buttons.managePreferences || fallback.managePreferences)}</h2>
    <p class="cmp-prefs__intro">${escapeHtml(introBody)}</p>
    ${categoriesHtml}
    <div class="cmp-prefs__footer">
      <button class="cmp-btn cmp-btn--manage" id="cmp-prefs-close">${escapeHtml(fallback.close)}</button>
      <button class="cmp-btn cmp-btn--save" id="cmp-prefs-save">${escapeHtml(buttons.save || fallback.save)}</button>
    </div>
  `;

  document.body.appendChild(modal);

  // Events
  modal.querySelector("#cmp-prefs-close")?.addEventListener("click", () => {
    closePreferences();
    onClose();
  });

  modal.querySelector("#cmp-prefs-save")?.addEventListener("click", () => {
    const checkboxes = modal.querySelectorAll<HTMLInputElement>("input[data-cmp-cat]");
    for (const cb of checkboxes) {
      const key = cb.getAttribute("data-cmp-cat");
      if (key) state.categories[key] = cb.checked;
    }

    const cookieCheckboxes = modal.querySelectorAll<HTMLInputElement>("input[data-cmp-cookie]");
    for (const cb of cookieCheckboxes) {
      const key = cb.getAttribute("data-cmp-cookie");
      if (key) state.cookies[key] = cb.checked;
    }

    // Keep required category cookies always enabled.
    for (const cat of categories) {
      if (!cat.required || !state.categories[cat.key]) continue;
      for (const cookie of config.cookies || []) {
        if (cookie.category === cat.key) state.cookies[cookie.key] = true;
      }
    }

    // If category is turned off, enforce its cookie toggles off as well.
    for (const cookie of config.cookies || []) {
      if (!state.categories[cookie.category]) {
        state.cookies[cookie.key] = false;
      }
    }
    closePreferences();
    onSave(state);
  });

  if (!isStrictPageBlock()) {
    overlay.addEventListener("click", () => {
      closePreferences();
      onClose();
    });
  }

  return modal;
}

export function closePreferences(): void {
  document.getElementById("cmp-prefs")?.remove();
  document.getElementById("cmp-overlay")?.remove();
  releasePageLockIfIdle();
  syncConsentWidget();
}

function escapeHtml(str: string): string {
  const div = document.createElement("div");
  div.textContent = str;
  return div.innerHTML;
}
